import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Container, Nav, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import QuestionsList from '../../../Components/ChecklistComponents/QuestionsList';

const BASE_URL = process.env.REACT_APP_API_URL;

const AuditOverview = () => {
  const [auditChecklists, setAuditChecklists] = useState([]);
  const [reauditChecklists, setReauditChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('audit');
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [sectionNames, setSectionNames] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [currentLevel, setCurrentLevel] = useState('checklist');
  const [sectionData, setSectionData] = useState(null);
  const [sectionsProgress, setSectionsProgress] = useState({});
  const [subsectionProgress, setSubsectionProgress] = useState({});
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  
  const location = useLocation();
  const navigate = useNavigate();

  const auditorData = JSON.parse(localStorage.getItem("auditor"));
  const auditorId = auditorData?.id;
  const BranchCode = auditorData?.BranchCode;

  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  // Set activeTab from navigation state on component mount
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  useEffect(() => {
    if (auditorId && BranchCode) {
      fetchAllChecklists();
    }
  }, [auditorId, BranchCode]);

  const fetchAllChecklists = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAuditChecklists(),
        fetchReauditChecklists()
      ]);
    } catch (error) {
      console.error('Error fetching checklists:', error);
      notify('Failed to load checklists', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditChecklists = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-assigned-checklists/${auditorId}/${BranchCode}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch audit checklists');
      
      const data = await response.json();
      // Filter to show only pending checklists
      const allChecklists = Array.isArray(data) ? data : [];
      const pendingChecklists = allChecklists.filter(checklist => 
        checklist.status?.toLowerCase() === 'pending'
      );
      setAuditChecklists(pendingChecklists);
      
    } catch (error) {
      console.error('Error fetching audit checklists:', error);
      notify('Failed to load audit checklists', 'error');
    }
  };

  const fetchReauditChecklists = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-reaudit-checklists/${auditorId}/${BranchCode}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch re-audit checklists');
      
      const data = await response.json();
      // Filter to show only pending re-audit checklists
      const allChecklists = Array.isArray(data) ? data : [];
      const pendingChecklists = allChecklists.filter(checklist => 
        checklist.status?.toLowerCase() === 'pending'
      );
      setReauditChecklists(pendingChecklists);
      
    } catch (error) {
      console.error('Error fetching re-audit checklists:', error);
      notify('Failed to load re-audit checklists', 'error');
    }
  };

  const fetchChecklistSections = async (checklist, type) => {
    try {
      let sectionsResponse;
      
      if (type === 'audit') {
        sectionsResponse = await fetch(
          `${BASE_URL}/get-checklist-sections/${checklist.checklist_id}/${checklist.BranchCode}`
        );
      } else {
        sectionsResponse = await fetch(
          `${BASE_URL}/get-reaudit-details/${checklist.id}`
        );
      }
  
      if (!sectionsResponse.ok) throw new Error('Failed to fetch sections');
      
      const sectionsData = await sectionsResponse.json();
      let sections = [];
  
      if (type === 'audit') {
        sections = sectionsData.sections || [];
      } else {
        // Fix: Properly handle re-audit sections
        if (sectionsData.sections) {
          try {
            const parsedSections = typeof sectionsData.sections === 'string' 
              ? JSON.parse(sectionsData.sections) 
              : sectionsData.sections;
            
            // Handle both array of objects and array of strings
            if (Array.isArray(parsedSections)) {
              sections = parsedSections.map(s => 
                typeof s === 'object' && s.name ? s.name : s
              );
            }
          } catch (parseError) {
            console.error('Error parsing re-audit sections:', parseError);
            sections = [];
          }
        }
      }
  
      setSectionNames(sections);
      return sections;
    } catch (error) {
      console.error('Error fetching sections:', error);
      notify('Failed to load sections', 'error');
      return [];
    }
  };

  const fetchAllSectionsQuestionCount = async (checklist, type, sections) => {
    try {
      const progressMap = {};


      // Process each section recursively like AuditSections does
      const processSection = async (path) => {
        const pathString = path.join('|');
        
        // Fetch progress for current path
        let progressResponse;
        if (type === 'audit') {
          progressResponse = await fetch(
            `${BASE_URL}/get-section-progress/${checklist.checklist_id}/${checklist.BranchCode}/${auditorId}/${checklist.branch_id}/${pathString}`
          );
        } else {
          progressResponse = await fetch(
            `${BASE_URL}/get-reaudit-section-progress/${checklist.id}/${encodeURIComponent(pathString)}`
          );
        }
        
        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          progressMap[pathString] = progressData;

          // Fetch section details to check for subsections
          let detailsResponse;
          if (type === 'audit') {
            detailsResponse = await fetch(
              `${BASE_URL}/get-nested-section-details/${checklist.checklist_id}/${checklist.BranchCode}/${pathString}`
            );
          } else {
            detailsResponse = await fetch(
              `${BASE_URL}/get-reaudit-nested-details/${checklist.id}/${encodeURIComponent(pathString)}`
            );
          }
          
          if (detailsResponse.ok) {
            const sectionData = await detailsResponse.json();

            // Recursively process subsections if they exist
            if (sectionData.subsections?.length > 0) {
              for (const subsection of sectionData.subsections) {
                await processSection([...path, subsection.name]);
              }
            }
          }
        } else {
          progressMap[pathString] = {
            progressPercentage: 0,
            answeredQuestions: 0,
            totalQuestions: 0
          };
        }
      };

      // Start processing from main sections
      for (const section of sections) {
        await processSection([section]);
      }

      setSectionsProgress(progressMap);
    } catch (error) {
      console.error('Error fetching question counts:', error);
      notify('Failed to load question counts', 'error');
    }
  };

  const fetchSubsectionQuestionCount = async (subsectionPath) => {
    try {
      // Ensure subsectionPath is properly formatted
      const pathString = Array.isArray(subsectionPath) 
        ? subsectionPath.join('|')
        : subsectionPath;


      // First fetch progress for the current path
      let progressResponse;
      if (selectedChecklist.type === 'audit') {
        progressResponse = await fetch(
          `${BASE_URL}/get-section-progress/${selectedChecklist.checklist_id}/${selectedChecklist.BranchCode}/${auditorId}/${selectedChecklist.branch_id}/${pathString}`
        );
      } else {
        progressResponse = await fetch(
          `${BASE_URL}/get-reaudit-section-progress/${selectedChecklist.id}/${encodeURIComponent(pathString)}`
        );
      }

      if (!progressResponse.ok) {
        throw new Error(`Failed to fetch progress for path: ${pathString}`);
      }

      const progressData = await progressResponse.json();
      
      // Update progress state for current path
      setSubsectionProgress(prev => ({
        ...prev,
        [pathString]: progressData
      }));

      // Then check for nested subsections
      let detailsResponse;
      if (selectedChecklist.type === 'audit') {
        detailsResponse = await fetch(
          `${BASE_URL}/get-nested-section-details/${selectedChecklist.checklist_id}/${selectedChecklist.BranchCode}/${pathString}`
        );
      } else {
        detailsResponse = await fetch(
          `${BASE_URL}/get-reaudit-nested-details/${selectedChecklist.id}/${encodeURIComponent(pathString)}`
        );
      }

      if (detailsResponse.ok) {
        const details = await detailsResponse.json();
        
        // If there are nested subsections, fetch their progress recursively
        if (details.subsections?.length > 0) {
          await Promise.all(details.subsections.map(async (nestedSubsection) => {
            const nestedPath = Array.isArray(subsectionPath)
              ? [...subsectionPath, nestedSubsection.name]
              : `${pathString}|${nestedSubsection.name}`;
              
            try {
              let nestedProgressResponse;
              if (selectedChecklist.type === 'audit') {
                nestedProgressResponse = await fetch(
                  `${BASE_URL}/get-section-progress/${selectedChecklist.checklist_id}/${selectedChecklist.BranchCode}/${auditorId}/${selectedChecklist.branch_id}/${Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath}`
                );
              } else {
                nestedProgressResponse = await fetch(
                  `${BASE_URL}/get-reaudit-section-progress/${selectedChecklist.id}/${encodeURIComponent(Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath)}`
                );
              }

              if (nestedProgressResponse.ok) {
                const nestedProgressData = await nestedProgressResponse.json();
                setSubsectionProgress(prev => ({
                  ...prev,
                  [Array.isArray(nestedPath) ? nestedPath.join('|') : nestedPath]: nestedProgressData
                }));
              }
            } catch (nestedError) {
              console.error(`Error fetching nested progress for ${nestedSubsection.name}:`, nestedError);
            }
          }));
        }
      }

      return progressData;
    } catch (error) {
      console.error('Error fetching subsection progress:', error);
      return null;
    }
  };

// Modified useEffect for subsection progress fetching
useEffect(() => {
  const fetchProgress = async () => {
    if (subsections.length > 0 && currentPath.length > 0 && selectedChecklist) {
      try {
        // Use safeArrayAccess to prevent errors
        const safeSubsections = safeArrayAccess(subsections);
        
        await Promise.all(safeSubsections.map(async (subsection) => {
          try {
            const subsectionName = getSubsectionName(subsection);
            if (subsectionName) {
              const subsectionPath = [...currentPath, subsectionName];
              await fetchSubsectionQuestionCount(subsectionPath);
            }
          } catch (error) {
            logError(`fetchProgress for subsection`, error, subsection);
          }
        }));
      } catch (error) {
        logError('fetchProgress useEffect', error, { subsections, currentPath });
      }
    }
  };

  fetchProgress();
}, [subsections, currentPath, selectedChecklist]);

  const handleChecklistClick = async (checklist, type) => {
    setSelectedChecklist({ ...checklist, type });
    setCurrentLevel('section');
    setCurrentPath([]);
    setSelectedSection(null);
    setQuestions([]);
    setSubsections([]);
    setAnswers({});
    
    
    const sections = await fetchChecklistSections(checklist, type);
    if (sections.length > 0) {
      await fetchAllSectionsQuestionCount(checklist, type, sections);
    }
  };

  const handleSectionClick = async (sectionName, index) => {
    setSelectedSection(sectionName);
    setIsSectionLoading(true);
    setCurrentPath([sectionName]);
    setCurrentLevel('section');
  
    try {
      let response;
      
      if (selectedChecklist.type === 'audit') {
        response = await fetch(
          `${BASE_URL}/get-section-details/${selectedChecklist.checklist_id}/${selectedChecklist.BranchCode}/${sectionName}`
        );
      } else {
        response = await fetch(
          `${BASE_URL}/get-reaudit-section-details/${selectedChecklist.id}/${encodeURIComponent(sectionName)}`
        );
      }
  
      if (!response.ok) {
        throw new Error('Failed to fetch section details');
      }
  
      const data = await response.json();
      setSectionData(data);
      
      // Fix: Handle undefined subsections and questions
      const hasQuestions = data.questions && Array.isArray(data.questions) && data.questions.length > 0;
      const hasSubsections = data.subsections && Array.isArray(data.subsections) && data.subsections.length > 0;
      const hasData = data.data && Array.isArray(data.data) && data.data.length > 0;
      
      // Determine content type more reliably
      if (data.type === 'both' || (hasQuestions && hasSubsections)) {
        setQuestions(data.questions || []);
        setSubsections(data.subsections || []);
      } else if (data.type === 'questions' || (hasQuestions && !hasSubsections)) {
        setQuestions(data.questions || data.data || []);
        setSubsections([]);
      } else if (data.type === 'subsections' || (hasSubsections && !hasQuestions)) {
        setQuestions([]);
        setSubsections(data.subsections || data.data || []);
      } else if (hasData) {
        // Fallback: try to determine if data contains questions or subsections
        const firstItem = data.data[0];
        if (firstItem && (firstItem.question || firstItem.question_text)) {
          setQuestions(data.data);
          setSubsections([]);
        } else {
          setQuestions([]);
          setSubsections(data.data);
        }
      } else {
        setQuestions([]);
        setSubsections([]);
        notify('No content found for this section', 'info');
      }
    } catch (error) {
      console.error('Error:', error);
      notify(error.message, 'error');
      // Reset states on error
      setQuestions([]);
      setSubsections([]);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const handleItemClick = async (itemName, itemType, parentPath = []) => {
    setIsSectionLoading(true);
    const newPath = [...parentPath, itemName];
    setCurrentPath(newPath);
    
    try {
      const pathParam = newPath.join('|');
      let response;
  
      if (selectedChecklist.type === 'audit') {
        response = await fetch(
          `${BASE_URL}/get-nested-section-details/${selectedChecklist.checklist_id}/${selectedChecklist.BranchCode}/${pathParam}`
        );
      } else {
        response = await fetch(
          `${BASE_URL}/get-reaudit-nested-details/${selectedChecklist.id}/${encodeURIComponent(pathParam)}`
        );
      }
  
      if (!response.ok) {
        throw new Error('Failed to fetch details');
      }
  
      const data = await response.json();
      setSectionData(data);
      
      const nextLevel = newPath.length === 1 ? 'subsection' : 
                       newPath.length === 2 ? 'subsubsection' : 
                       'final';
      setCurrentLevel(nextLevel);
  
      // Fix: Handle undefined data properties with better error checking
      const hasQuestions = data.questions && Array.isArray(data.questions) && data.questions.length > 0;
      const hasSubsections = data.subsections && Array.isArray(data.subsections) && data.subsections.length > 0;
      const hasData = data.data && Array.isArray(data.data) && data.data.length > 0;
  
      if (data.type === 'both' || (hasQuestions && hasSubsections)) {
        setQuestions(data.questions || []);
        setSubsections(data.subsections || []);
      } else if (data.type === 'questions' || (hasQuestions && !hasSubsections)) {
        setQuestions(data.questions || data.data || []);
        setSubsections([]);
      } else if (data.type === 'subsections' || (hasSubsections && !hasQuestions)) {
        setQuestions([]);
        setSubsections(data.subsections || data.data || []);
      } else if (hasData) {
        // Try to determine content type from data structure
        const firstItem = data.data[0];
        if (firstItem && (firstItem.question || firstItem.question_text || firstItem.id)) {
          // Looks like questions
          setQuestions(data.data);
          setSubsections([]);
        } else if (firstItem && (firstItem.name || typeof firstItem === 'string')) {
          // Looks like subsections
          setQuestions([]);
          setSubsections(data.data.map(item => 
            typeof item === 'object' ? item : { name: item }
          ));
        } else {
          setQuestions([]);
          setSubsections([]);
        }
      } else {
        setQuestions([]);
        setSubsections([]);
        notify('No content found', 'info');
      }
  
      // Fetch progress for subsections if they exist
      if (hasSubsections || (hasData && data.data.some(item => item.name || typeof item === 'string'))) {
        const subsectionsToProcess = data.subsections || data.data || [];
        await Promise.all(subsectionsToProcess.map(async (subsection) => {
          const subsectionName = subsection.name || subsection;
          const subsectionPath = [...newPath, subsectionName];
          await fetchSubsectionQuestionCount(subsectionPath);
        }));
      }
  
    } catch (error) {
      console.error('Error:', error);
      notify(error.message, 'error');
      // Reset states on error
      setQuestions([]);
      setSubsections([]);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const navigateToPath = (index) => {
    const newPath = currentPath.slice(0, index + 1);
    handleItemClick(newPath[newPath.length - 1], 'section', newPath.slice(0, -1));
  };

  const resetNavigation = () => {
    setSelectedSection(null);
    setSubsections([]);
    setQuestions([]);
    setCurrentPath([]);
    setCurrentLevel('section');
    setAnswers({});
  };

  const resetToChecklists = () => {
    setSelectedChecklist(null);
    setSectionNames([]);
    resetNavigation();
    setCurrentLevel('checklist');
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in progress':
        return 'warning';
      case 'pending':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const handleAnswerChange = () => {};
  const handleSubmit = () => {};

  // Add this function to safely access nested properties
const safeArrayAccess = (arr, defaultValue = []) => {
  return Array.isArray(arr) ? arr : defaultValue;
};

// Add this function to safely get subsection name
const getSubsectionName = (subsection) => {
  if (typeof subsection === 'string') return subsection;
  if (typeof subsection === 'object' && subsection !== null) {
    return subsection.name || subsection.title || '';
  }
  return '';
};

// Add better error logging
const logError = (context, error, data = null) => {
  console.error(`Error in ${context}:`, error);
  if (data) {
    console.error('Related data:', data);
  }
};

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center py-5">
          <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-primary fw-bold mb-1">Loading Audit Overview</p>
          <p className="text-muted">Please wait while we fetch your audit data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer />
      
      {/* Header with Breadcrumb */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2 className="text-primary mb-2">Audit Overview</h2>
              <p className="text-muted">Preview and explore your pending audit and re-audit checklists with their questions</p>
              
              
            </div>
          </div>
          
          {/* Breadcrumb Navigation */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  resetToChecklists();
                }}>Overview</a>
              </li>
              
              {selectedChecklist && (
                <li className="breadcrumb-item">
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    resetNavigation();
                  }}>
                    {selectedChecklist.checklist_name} - Sections
                  </a>
                </li>
              )}
              
              {currentPath.map((pathItem, index) => (
                <li 
                  key={index} 
                  className={`breadcrumb-item ${index === currentPath.length - 1 ? 'active' : ''}`}
                >
                  {index === currentPath.length - 1 ? (
                    <span>{pathItem}</span>
                  ) : (
                    <a href="#" onClick={(e) => {
                      e.preventDefault();
                      navigateToPath(index);
                    }}>{pathItem}</a>
                  )}
                </li>
              ))}
              
              {questions.length > 0 && currentPath.length > 0 && (
                <li className="breadcrumb-item active">Questions Preview</li>
              )}
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Main Content */}
      {!selectedChecklist ? (
        <>
          {/* Navigation Tabs */}
<div className="mb-4">
  <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="d-none d-sm-flex">
    <Nav.Item>
      <Nav.Link eventKey="audit" className="d-flex align-items-center gap-2">
        <i className="fas fa-clipboard-list"></i>
        Audit
        <Badge bg="danger" pill>{auditChecklists.length}</Badge>
      </Nav.Link>
    </Nav.Item>
    <Nav.Item>
      <Nav.Link eventKey="reaudit" className="d-flex align-items-center gap-2">
        <i className="fas fa-redo"></i>
        CAP
        <Badge bg="danger" pill>{reauditChecklists.length}</Badge>
      </Nav.Link>
    </Nav.Item>
  </Nav>

  {/* Mobile 2-Row Layout */}
  <div className="d-sm-none">
    <div className="row g-2">
      <div className="col-6">
        <button 
          className={`btn w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'audit' ? 'btn-danger' : 'btn-outline-danger'}`}
          onClick={() => setActiveTab('audit')}
        >
          <i className="fas fa-clipboard-list"></i>
          Audit
          <Badge bg={activeTab === 'audit' ? 'light' : 'danger'} text={activeTab === 'audit' ? 'dark' : 'light'} pill>
            {auditChecklists.length}
          </Badge>
        </button>
      </div>
      <div className="col-6">
        <button 
          className={`btn w-100 d-flex align-items-center justify-content-center gap-2 ${activeTab === 'reaudit' ? 'btn-danger' : 'btn-outline-danger'}`}
          onClick={() => setActiveTab('reaudit')}
        >
          <i className="fas fa-redo"></i>
          CAP
          <Badge bg={activeTab === 'reaudit' ? 'light' : 'danger'} text={activeTab === 'reaudit' ? 'dark' : 'light'} pill>
            {reauditChecklists.length}
          </Badge>
        </button>
      </div>
    </div>
  </div>
</div>

          {/* Checklists Grid */}
          <Row xs={1} md={2} lg={4} className="g-4">
            {activeTab === 'audit' ? (
              auditChecklists.length > 0 ? (
                auditChecklists.map((checklist) => (
                  <Col key={`audit_${checklist.checklist_id}`}>
                    <Card 
                      className="h-100 shadow-sm hover-shadow border-danger"
                      onClick={() => handleChecklistClick(checklist, 'audit')}
                      role="button"
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="text-center mb-3">
                          <div className="preview-icon-danger mb-3">
                            <i className="fas fa-clipboard-list text-danger" style={{ fontSize: '3rem' }}></i>
                          </div>
                          <h5 className="text-primary mb-1">{checklist.checklist_name}</h5>
                          <p className="text-muted small mb-2">
                            <i className="fas fa-building me-1"></i>
                            {checklist.branches}
                          </p>
                          
                        </div>
                        <div className="mt-auto text-center">
                          <small className="text-muted">
                            <i className="fas fa-eye me-1"></i>
                            Click to preview sections & questions
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col xs={12}>
                  <div className="text-center py-5">
                    <i className="fas fa-clipboard-check fa-3x text-success mb-3"></i>
                    <h4 className="text-success mb-2">No Plan Audit Checklists</h4>
                    <p className="text-muted">Great! You don't have any pending audit checklists. All your audits are up to date.</p>
                  </div>
                </Col>
              )
            ) : (
              reauditChecklists.length > 0 ? (
                reauditChecklists.map((checklist) => (
                  <Col key={`reaudit_${checklist.id}`}>
                    <Card 
                      className="h-100 shadow-sm hover-shadow border-danger"
                      onClick={() => handleChecklistClick(checklist, 'reaudit')}
                      role="button"
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="text-center mb-3">
                          <div className="preview-icon-danger mb-3">
                            <i className="fas fa-redo text-danger" style={{ fontSize: '3rem' }}></i>
                          </div>
                          <h5 className="text-primary mb-1">{checklist.checklist_name}</h5>
                          <p className="text-muted small mb-2">
                            <i className="fas fa-building me-1"></i>
                            {checklist.branches}
                          </p>
                          <Badge bg="danger" className="px-2 py-1">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            Plan
                          </Badge>
                        </div>
                        <div className="mt-auto text-center">
                          <small className="text-muted">
                            <i className="fas fa-eye me-1"></i>
                            Click to preview sections & questions
                          </small>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col xs={12}>
                  <div className="text-center py-5">
                    <i className="fas fa-check-double fa-3x text-success mb-3"></i>
                    <h4 className="text-success mb-2">No Plan Re-Audit Checklists</h4>
                    <p className="text-muted">Excellent! You don't have any pending re-audit checklists. All your re-audits are complete.</p>
                  </div>
                </Col>
              )
            )}
          </Row>
        </>
      ) : (
        <>
          {/* Selected Checklist View */}
          {currentPath.length === 0 ? (
            // Sections Grid
            <Row xs={1} md={2} lg={4} className="g-4">
              {sectionNames.map((sectionName, index) => {
                const progress = sectionsProgress[sectionName] || {
                  progressPercentage: 0,
                  answeredQuestions: 0,
                  totalQuestions: 0
                };


                return (
                  <Col key={index}>
                    <Card 
                      className="h-100 shadow-sm hover-shadow"
                      onClick={() => handleSectionClick(sectionName, index)}
                      role="button"
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="text-center mb-3">
                          <div className="section-icon mb-3">
                            <i className="fas fa-folder-open text-primary" style={{ fontSize: '3rem' }}></i>
                          </div>
                          <h5 className="mb-1">{sectionName}</h5>
                          <Badge bg="secondary" className="px-3 py-1 mb-2">
                            {progress.totalQuestions} Questions
                          </Badge>
                        </div>
                        <div className="mt-auto text-center">
                          <Button variant="outline-primary" size="sm">
                            <i className="fas fa-eye me-1"></i>
                            Preview Questions
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <>
              {/* Loading State */}
              {isSectionLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-bold mb-1">Loading Questions</p>
                  <p className="text-muted">Please wait...</p>
                </div>
              ) : (
                <>
                  {/* Questions Display using QuestionsList Component */}
                  {questions.length > 0 && (
                    <div className="questions-section preview-mode">
                      
                      <QuestionsList
                        questions={questions}
                        answers={answers}
                        handleAnswerChange={handleAnswerChange}
                        setAnswers={setAnswers}
                        selectedSection={currentPath[currentPath.length - 1]}
                        handleSubmit={handleSubmit}
                        isViewOnly={true}
                      />
                    </div>
                  )}
{/* Subsections Display - Fixed */}
{subsections.length > 0 && !questions.length && (
  <Row xs={1} md={2} lg={4} className="g-4">
    {subsections.map((subsection, index) => {
      // Handle both object and string subsection formats
      const subsectionName = subsection.name || subsection;
      const subsectionPath = [...currentPath, subsectionName].join('|');
      const progress = subsectionProgress[subsectionPath] || {
        progressPercentage: 0,
        answeredQuestions: 0,
        totalQuestions: 0
      };


      return (
        <Col key={index}>
          <Card 
            className="h-100 shadow-sm hover-shadow"
            onClick={() => handleItemClick(
              subsectionName, 
              currentLevel, 
              currentPath
            )}
            role="button"
          >
            <Card.Body className="d-flex flex-column">
              <div className="text-center mb-3">
                <div className="subsection-icon mb-3">
                  <i className="fas fa-folder text-info" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h5 className="mb-1">{subsectionName}</h5>
                <Badge bg="secondary" className="px-3 py-1 mb-2">
                  {progress.totalQuestions} Questions
                </Badge>
              </div>
              <div className="mt-auto text-center">
                <Button variant="outline-info" size="sm">
                  <i className="fas fa-arrow-right me-1"></i>
                  Explore
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      );
    })}
  </Row>
)}

                  {!questions.length && !subsections.length && (
                    <div className="text-center py-5">
                      <i className="fas fa-folder-open fa-4x text-muted mb-3"></i>
                      <h4 className="text-muted">No content found</h4>
                      <Button 
                        variant="outline-primary" 
                        className="mt-3"
                        onClick={resetNavigation}
                      >
                        Return to Sections
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Navigation Buttons */}
          {(questions.length > 0 || subsections.length > 0 || currentPath.length > 0) && !isSectionLoading && (
            <div className="navigation-buttons d-flex justify-content-between mt-4"
            style={{
              position: 'fixed',
              bottom: '1rem',
              left: '1rem',
              right: '1rem',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '1rem',
              borderRadius: '0.5rem',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            }}
            >
              <Button 
                variant="outline-secondary"
                onClick={() => {
                  if (currentPath.length === 1) {
                    resetNavigation();
                  } else if (currentPath.length > 1) {
                    navigateToPath(currentPath.length - 2);
                  } else {
                    resetToChecklists();
                  }
                }}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </Button>
              
              
            </div>
          )}
        </>
      )}

      <style>
        {`
          .hover-shadow:hover {
            transform: translateY(-5px);
            cursor: pointer;
            transition: transform 0.2s ease-in-out;
            box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
          }
          
          .border-danger {
            border-left: 4px solid #dc3545 !important;
          }
          
          .breadcrumb-item a {
            text-decoration: none;
            color: #0d6efd;
          }
          
          .breadcrumb-item a:hover {
            text-decoration: underline;
          }

          .navigation-buttons {
            position: sticky;
            bottom: 1rem;
            z-index: 100;
            padding: 1rem;
            background-color: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(8px);
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          }

          .nav-tabs {
            border-bottom: 2px solid #dee2e6;
          }
          
          .nav-tabs .nav-link {
            color: #6c757d;
            border: none;
            border-bottom: 2px solid transparent;
            padding: 0.75rem 1rem;
            font-weight: 500;
            transition: all 0.3s ease;
          }
          
          .nav-tabs .nav-link:hover {
            border-bottom-color: #0d6efd;
            color: #0d6efd;
          }
          
          .nav-tabs .nav-link.active {
            color: #0d6efd;
            background-color: transparent;
            border-bottom-color: #0d6efd;
          }

          .preview-icon-danger {
            width: 80px;
            height: 80px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(220, 53, 69, 0.1);
            border-radius: 20px;
          }

          .subsection-icon {
            width: 60px;
            height: 60px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(23, 162, 184, 0.1);
            border-radius: 12px;
          }

          .section-icon {
            width: 80px;
            height: 80px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(13, 110, 253, 0.1);
            border-radius: 20px;
          }

          .preview-mode .alert-info {
            border-left: 4px solid #0dcaf0;
            background-color: #cff4fc;
            border-color: #b8e6f0;
            color: #055160;
          }

          .questions-section.preview-mode {
            background-color: #ffffff;
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }

          .card {
            transition: all 0.3s ease;
          }
          
          .card:hover {
            transform: translateY(-5px);
            box-shadow: 0 5px 15px rgba(0,0,0,0.1) !important;
          }

          .badge {
            font-weight: 500;
            padding: 0.5em 1em;
          }

          @media (max-width: 768px) {
            .navigation-buttons {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              border-radius: 0;
              border-top: 1px solid rgba(0,0,0,0.1);
            }
          }

          .spinner-grow {
            animation-duration: 1.5s;
          }
        `}
      </style>
    </Container>
  );
};

export default AuditOverview;
